import { useEffect, useMemo, useState } from 'react';
import type { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import type {
  FilterState,
  PaginationMeta,
  SortState,
  TableColumn,
} from '../../../components/ui/tableTemplate/TableTemplateTypes';
import { DEFAULT_PAGE, DEFAULT_LIMIT } from '../../../hooks/usePaginationState';
import { PayslipApiFactory } from '../api/payslipApi';
import type { Payslip } from '../payslipTypes';

export { DEFAULT_PAGE, DEFAULT_LIMIT };

interface UseGetPayslipsResult {
  data: Payslip[];
  loading: boolean;
  error: ApiError | Error | null;
  meta: PaginationMeta | null;
}

const EMPTY_FILTER_STATE: FilterState<Payslip> = {};

/**
 * Fetches an employee's payslips from the API. On failure the hook surfaces the
 * error and returns no rows — the caller shows an error message (no mock/demo
 * fallback: sample data must never be mistaken for real payslips).
 */
export function useGetPayslips(
  employeeId: string,
  columns: TableColumn<Payslip>[],
  sortState: SortState<Payslip> | null,
  filterState: FilterState<Payslip> = EMPTY_FILTER_STATE,
  page: number = DEFAULT_PAGE,
  limit: number = DEFAULT_LIMIT,
): UseGetPayslipsResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => PayslipApiFactory(client), [client]);

  const [data, setData] = useState<Payslip[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);

  useEffect(() => {
    if (!employeeId) return;

    const ctrl = new AbortController();

    api
      .getAllByEmployeeId(
        employeeId,
        {
          sortBy: sortState?.sortBy != null ? String(sortState.sortBy) : undefined,
          sortOrder: sortState?.sortOrder,
          filters: filterState,
          columns,
          page,
          limit,
        },
        { signal: ctrl.signal },
      )
      .then(response => {
        setError(null);
        setData(response.data);
        setMeta(response.meta);
      })
      .catch(err => {
        if (err instanceof Error && err.name === 'AbortError') return;
        // API unavailable/failed → surface the error and show no rows.
        setError(err instanceof Error ? err : new Error(String(err)));
        setData([]);
        setMeta(null);
      });

    return () => ctrl.abort();
  }, [api, employeeId, columns, sortState, filterState, page, limit]);

  return { data, loading, error, meta };
}
